import React, { useState, useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  CardHeader,
  useColorModeValue,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Input,
  InputGroup,
  InputLeftElement,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Button,
  IconButton,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Stack,
  Divider,
  Badge,
  Link,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Icon,
  Tooltip,
  List,
  ListItem,
  ListIcon,
  OrderedList,
  UnorderedList,
  Code,
  useColorMode,
} from '@chakra-ui/react';
import {
  FiSearch,
  FiHelpCircle,
  FiBookOpen,
  FiExternalLink,
  FiAlertTriangle,
  FiCheck,
  FiArrowLeft,
  FiPlay,
  FiShield,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePriceData } from '@/hooks/usePriceData';
import PageHeader from '@/components/common/PageHeader';

// FAQ 数据生成函数
const getFAQData = (t: any) => [
  {
    category: t('faq.categories.recharge'),
    items: [
      {
        question: t('faq.items.recharge.howToRecharge.question'),
        answer: t('faq.items.recharge.howToRecharge.answer'),
        link: "/recharge",
        linkText: t('faq.items.recharge.howToRecharge.linkText')
      },
      {
        question: t('faq.items.recharge.rechargeRatio.question'),
        answer: t('faq.items.recharge.rechargeRatio.answer'),
        link: "/recharge",
        linkText: t('faq.items.recharge.rechargeRatio.linkText')
      },
      {
        question: t('faq.items.recharge.supportedNetworks.question'),
        answer: t('faq.items.recharge.supportedNetworks.answer'),
        link: "/recharge",
        linkText: t('faq.items.recharge.supportedNetworks.linkText')
      }
    ]
  },
  {
    category: t('faq.categories.invite'),
    items: [
      {
        question: t('faq.items.invite.generateCode.question'),
        answer: t('faq.items.invite.generateCode.answer'),
        link: "/invite",
        linkText: t('faq.items.invite.generateCode.linkText')
      },
      {
        question: t('faq.items.invite.rewardMechanism.question'),
        answer: t('faq.items.invite.rewardMechanism.answer'),
        link: "/invite",
        linkText: t('faq.items.invite.rewardMechanism.linkText')
      },
      {
        question: t('faq.items.invite.unlockLocked.question'),
        answer: t('faq.items.invite.unlockLocked.answer'),
        link: "/invite",
        linkText: t('faq.items.invite.unlockLocked.linkText')
      }
    ]
  },
  {
    category: t('faq.categories.management'),
    items: [
      {
        question: t('faq.items.management.manageDOR.question'),
        answer: t('faq.items.management.manageDOR.answer'),
        link: "/management",
        linkText: t('faq.items.management.manageDOR.linkText')
      },
      {
        question: t('faq.items.management.conversionFee.question'),
        answer: t('faq.items.management.conversionFee.answer'),
        link: "/management",
        linkText: t('faq.items.management.conversionFee.linkText')
      },
      {
        question: t('faq.items.management.viewHistory.question'),
        answer: t('faq.items.management.viewHistory.answer'),
        link: "/history",
        linkText: t('faq.items.management.viewHistory.linkText')
      }
    ]
  },
  {
    category: t('faq.categories.price'),
    items: [
      {
        question: t('faq.items.price.priceStability.question'),
        answer: t('faq.items.price.priceStability.answer'),
        link: "/reserve",
        linkText: t('faq.items.price.priceStability.linkText')
      },
      {
        question: t('faq.items.price.priceBelowOne.question'),
        answer: t('faq.items.price.priceBelowOne.answer'),
        link: "/invite",
        linkText: t('faq.items.price.priceBelowOne.linkText'),
        highlight: true
      },
      {
        question: t('faq.items.price.viewReserve.question'),
        answer: t('faq.items.price.viewReserve.answer'),
        link: "/reserve",
        linkText: t('faq.items.price.viewReserve.linkText')
      }
    ]
  },
  {
    category: t('faq.categories.security'),
    items: [
      {
        question: t('faq.items.security.systemSecurity.question'),
        answer: t('faq.items.security.systemSecurity.answer'),
        link: "/reserve",
        linkText: t('faq.items.security.systemSecurity.linkText')
      },
      {
        question: t('faq.items.security.protectAssets.question'),
        answer: t('faq.items.security.protectAssets.answer'),
        link: "/settings",
        linkText: t('faq.items.security.protectAssets.linkText')
      },
      {
        question: t('faq.items.security.troubleshooting.question'),
        answer: t('faq.items.security.troubleshooting.answer'),
        link: "#contact",
        linkText: t('faq.items.security.troubleshooting.linkText')
      }
    ]
  }
];

// 教程数据生成函数
const getTutorialData = (t: any) => [
  {
    category: t('tutorials.categories.beginner'),
    items: [
      {
        title: t('tutorials.items.beginner.gettingStarted.title'),
        content: t('tutorials.items.beginner.gettingStarted.content')
      },
      {
        title: t('tutorials.items.beginner.mechanism.title'),
        content: t('tutorials.items.beginner.mechanism.content')
      }
    ]
  },
  {
    category: t('tutorials.categories.advanced'),
    items: [
      {
        title: t('tutorials.items.advanced.manageDOR.title'),
        content: t('tutorials.items.advanced.manageDOR.content')
      },
      {
        title: t('tutorials.items.advanced.inviteStrategy.title'),
        content: t('tutorials.items.advanced.inviteStrategy.content')
      }
    ]
  },
  {
    category: t('tutorials.categories.troubleshooting'),
    items: [
      {
        title: t('tutorials.items.troubleshooting.commonIssues.title'),
        content: t('tutorials.items.troubleshooting.commonIssues.content')
      },
      {
        title: t('tutorials.items.troubleshooting.priceDeviation.title'),
        content: t('tutorials.items.troubleshooting.priceDeviation.content')
      }
    ]
  }
];

// FAQ 组件
const FAQ: React.FC = () => {
  const { t } = useTranslation('help');
  const [searchTerm, setSearchTerm] = useState('');
  const toast = useToast();
  const navigate = useNavigate();
  const { priceData } = usePriceData();
  const price = priceData?.currentPrice || 1;
  
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // 获取FAQ数据
  const faqData = getFAQData(t);

  // 搜索过滤
  const filteredFAQ = useMemo(() => {
    if (!searchTerm) return faqData;
    
    const searchLower = searchTerm.toLowerCase();
    return faqData.map((category: any) => ({
      ...category,
      items: category.items.filter((item: any) => 
        item.question.toLowerCase().includes(searchLower) ||
        item.answer.toLowerCase().includes(searchLower)
      )
    })).filter((category: any) => category.items.length > 0);
  }, [searchTerm, faqData]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    if (value && filteredFAQ.length === 0) {
      toast({
        title: t('faq.noResults.title'),
        description: t('faq.noResults.description'),
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Card bg={cardBg} border="1px" borderColor={borderColor}>
      <CardHeader>
        <VStack align="stretch" spacing={4}>
          <Heading size="md">{t('faq.title')}</Heading>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
                              <FiSearch color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder={t('faq.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </InputGroup>
        </VStack>
      </CardHeader>
      <CardBody>
        <VStack spacing={4} align="stretch">
          {filteredFAQ.length > 0 ? (
            <Accordion allowMultiple>
              {filteredFAQ.map((category: any, categoryIndex: number) => (
                <Box key={categoryIndex}>
                  <Text fontWeight="bold" fontSize="lg" mb={3} color="blue.500">
                    {category.category}
                  </Text>
                  {category.items.map((item: any, itemIndex: number) => (
                    <AccordionItem key={itemIndex} border="1px" borderColor={borderColor} mb={2} borderRadius="md">
                      <AccordionButton 
                        py={4}
                        _expanded={{ bg: useColorModeValue('blue.50', 'blue.900') }}
                      >
                        <Box flex="1" textAlign="left">
                          <HStack>
                            <Text fontWeight="medium">{item.question}</Text>
                            {item.highlight && price < 0.99 && (
                              <Badge colorScheme="yellow" size="sm">{t('badges.priceDeviation')}</Badge>
                            )}
                          </HStack>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel pb={4}>
                        <VStack align="stretch" spacing={3}>
                          <Text>{item.answer}</Text>
                          {item.link && (
                            <Button
                              size="sm"
                              colorScheme="blue"
                              leftIcon={<FiExternalLink />}
                              onClick={() => navigate(item.link)}
                            >
                              {item.linkText}
                            </Button>
                          )}
                        </VStack>
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Box>
              ))}
            </Accordion>
          ) : (
            <Box textAlign="center" py={8}>
              <Box mb={4}><FiHelpCircle size={48} color="var(--chakra-colors-gray-400)" /></Box>
              <Text color="gray.500" mb={2}>{t('faq.emptyState.title')}</Text>
              <Text fontSize="sm" color="gray.400">
                {t('faq.emptyState.description')}
              </Text>
            </Box>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

// 教程组件
const Tutorials: React.FC = () => {
  const { t } = useTranslation('help');
  const [selectedTutorial, setSelectedTutorial] = useState<any>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const { priceData } = usePriceData();
  const price = priceData?.currentPrice || 1;
  
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // 获取教程数据
  const tutorialData = getTutorialData(t);

  const handleTutorialClick = (tutorial: any) => {
    setSelectedTutorial(tutorial);
    onOpen();
  };

  return (
    <>
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardHeader>
          <Heading size="md">{t('tutorials.title')}</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={6} align="stretch">
            {tutorialData.map((category: any, categoryIndex: number) => (
              <Box key={categoryIndex}>
                <Text fontWeight="bold" fontSize="lg" mb={4} color="blue.500">
                  {category.category}
                </Text>
                <VStack spacing={4} align="stretch">
                  {category.items.map((tutorial: any, tutorialIndex: number) => (
                    <Card key={tutorialIndex} variant="outline" cursor="pointer" onClick={() => handleTutorialClick(tutorial)}>
                      <CardBody>
                        <HStack justify="space-between" align="center">
                          <VStack align="flex-start" spacing={1}>
                            <HStack>
                              <Text fontWeight="medium">{tutorial.title}</Text>
                              {tutorial.title.includes('价格偏离') && price < 0.99 && (
                                <Badge colorScheme="yellow" size="sm">{t('tutorials.currentlyRelevant')}</Badge>
                              )}
                            </HStack>
                            <Text fontSize="sm" color="gray.500">
                              {category.category} • {t('tutorials.clickToView')}
                            </Text>
                          </VStack>
                          <FiPlay color="blue.500" />
                        </HStack>
                      </CardBody>
                    </Card>
                  ))}
                </VStack>
              </Box>
            ))}
          </VStack>
        </CardBody>
      </Card>

      {/* 教程详情模态 */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{selectedTutorial?.title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text whiteSpace="pre-line">{selectedTutorial?.content}</Text>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onClose}>
              {t('tutorials.modal.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};



// 主组件
const Help: React.FC = () => {
  const { t } = useTranslation('help');
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <Box pt={4}>
      <VStack spacing={6} align="stretch">
        {/* 移动端标题 */}
        <PageHeader 
          title={t('title')} 
          subtitle={t('subtitle')}
        />

        {/* 免责声明 */}
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>{t('disclaimer.title')}</AlertTitle>
            <AlertDescription>
              {t('disclaimer.description')}
            </AlertDescription>
          </Box>
        </Alert>

        {/* 主要内容 */}
        <Tabs variant="enclosed" colorScheme="blue">
          <TabList>
            <Tab>
              <HStack>
                <FiHelpCircle />
                <Text>{t('tabs.faq')}</Text>
              </HStack>
            </Tab>
            <Tab>
              <HStack>
                <FiBookOpen />
                <Text>{t('tabs.tutorials')}</Text>
              </HStack>
            </Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <FAQ />
            </TabPanel>
            <TabPanel>
              <Tutorials />
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* 底部操作区 */}
        <Card bg={cardBg} border="1px" borderColor={borderColor}>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between" align="center">
                <Text fontSize="sm" color="gray.500">
                  {t('bottom.needMoreHelp')}
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open('/whitepaper.pdf', '_blank')}
                >
                  {t('bottom.viewWhitepaper')}
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
};

export default Help; 